import './styles/LanguageSelector.scss';

import { XCircle } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@whistler-ui/k2/components/Button';
import { Loading } from '@whistler-ui/k2/components/Loading';
import { useClassHelper } from '@whistler-ui/k2/hooks';
import Multiselect from 'multiselect-react-dropdown';
import { useMemo } from 'react';

import { LANGUAGE_KEYS, LANGUAGE_OPTIONS } from '../../constants';
import {
  useCreateLocalisation,
  useDeleteLocalisation,
  useGetWebsheetSettingsByBrandId,
} from '../../hooks';
import { flattenNestedObject } from './helpers/common';

interface Props {
  brandId: string;
  tag: string;
  environmentId: string;
  closeModalCallback: () => void;
  template: { [key: string]: unknown };
}

const componentName = 'c-LanguageSelector';

export const LanguageSelector = (props: Props) => {
  const c = useClassHelper(componentName);
  const queryClient = useQueryClient();

  const {
    data: settings,
    isFetchedAfterMount,
    isRefetching,
  } = useGetWebsheetSettingsByBrandId({
    environmentId: props.environmentId,
    brandId: props.brandId,
    tag: props.tag,
  });

  const isSettingLoading = isFetchedAfterMount && isRefetching;

  const {
    mutateAsync: createLocalisation,
    isPending: createLocalisationIsPending,
  } = useCreateLocalisation();
  const {
    mutateAsync: deleteLocalisation,
    isPending: deleteLocalisationIsPending,
  } = useDeleteLocalisation();

  const updatingLocalisation =
    createLocalisationIsPending ||
    deleteLocalisationIsPending ||
    isSettingLoading;

  const options = useMemo(() => {
    return LANGUAGE_OPTIONS.map(({ label, value }) => ({
      key: label,
      cat: value,
    }));
  }, []);

  const selectedLanguages = useMemo(() => {
    return settings?.localisationSettings.map(({ locale }) => ({
      key: LANGUAGE_KEYS[locale],
      cat: locale,
    }));
  }, [settings, props.tag, props.brandId]);

  const refetchSettingsAndLocalisations = () => {
    return queryClient.invalidateQueries({
      queryKey: ['get_websheet_settings_by_brand_id'],
    });
  };

  const handleSelect = async (
    selectedItem: { key: string; cat: string },
    isRemove: boolean,
  ) => {
    if (isRemove) {
      const payload = {
        tag: props.tag,
        brandId: props.brandId,
        locale: selectedItem.cat,
        environmentId: props.environmentId,
      };

      await deleteLocalisation(payload);
    } else {
      const payload = {
        tag: props.tag,
        brandId: props.brandId,
        environmentId: props.environmentId,
        localisation: {
          locale: selectedItem.cat,
          data: flattenNestedObject(props.template),
        },
      };

      await createLocalisation(payload);
    }

    refetchSettingsAndLocalisations();
  };

  const LanguageSelect = () => {
    return (
      <section className={c('__content')}>
        <Multiselect
          className={c('__multiselect')}
          displayValue="key"
          onSelect={(_, selectedItem) => handleSelect(selectedItem, false)}
          onRemove={(_, selectedItem) => handleSelect(selectedItem, true)}
          customCloseIcon={<XCircle size={24} weight="fill" color="#0099cc" />}
          showCheckbox
          avoidHighlightFirstOption
          selectedValues={selectedLanguages}
          options={options}
          placeholder=""
          style={{
            chips: {
              background: '#d0e8f4',
              color: '#3a474e',
            },
            multiselectContainer: {
              color: '#3a474e',
            },
          }}
        />
      </section>
    );
  };

  return (
    <div className={componentName}>
      <header>
        <b>Select all the languages you wish to define</b>
      </header>
      <LanguageSelect />
      {Boolean(updatingLocalisation) && <Loading.Overlay zIndex={999} />}
      <div className={c('__footer')}>
        <Button type="button" onClick={props.closeModalCallback}>
          Done
        </Button>
      </div>
    </div>
  );
};
